'use client';
import { ChangeEvent, useEffect, useState } from 'react';
import {
  ReadonlyURLSearchParams,
  usePathname,
  useSearchParams,
  useRouter,
} from 'next/navigation';
import Button from '@ui/Button';
import { cn, extractCityNameFromUrl } from '@/lib/utils';
import { useTranslations } from 'next-intl';
import { STOREFRONT } from '@/lib/utils/translation';
import SearchOptionButtons from './SearchOptionButtons';
import { SelectOptionType } from '@/lib/types/BaseType';
import SelectWithRemoteSearch from '../shared/SelectWithRemoteSearch';
import { env } from '@/constant/configs';
import { RestaurantTagsResponse } from '@/lib/types/customer/RestaurantTags';
import { storeFront } from '@/lib/utils/routeLinks';
import OnlineShopCard from '../home/OnlineShopCard';
import CityNavigateCard from '../home/CityNavigateCard';
import { City } from '@/lib/types/customer/City';
import useAppLocale from '@/hooks/useAppLocale';
import TagsContainer from './TagsContainer';
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import useRestaurantTags from '@/hooks/useRestaurantTags';
import { SHOP_TYPES } from '@/constant/configs';

export type SearchTypes = 'delivery' | 'pickup' | 'all';
export type VendorTypes = 'restaurant' | 'online_shop';
type Props = {
  shippingType: SearchTypes;
  vendorType?: VendorTypes;
  tagList?: RestaurantTagsResponse;
  showShippingButtons?: boolean;
  showTagsCarousel?: boolean;
  fetchTagsClientSide?: boolean;
};

const initialSelectOption: SelectOptionType = { id: -1, name: '' };
export default function SearchLayoutCommon({
  shippingType,
  vendorType,
  tagList: serverTagList,
  showShippingButtons = false,
  showTagsCarousel = true,
  fetchTagsClientSide = false,
}: Props) {
  const t = useTranslations();
  const pathname = usePathname();
  const router = useRouter();
  const { replace } = router;
  const searchParams: ReadonlyURLSearchParams = useSearchParams();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCityOption, setSelectedCityOption] =
    useState<SelectOptionType>(initialSelectOption);
  const cityQueryParam = searchParams.get('city') || '';
  const [cityData, setCityData] = useState<City>();
  const lang = useAppLocale().lang;

  // Fetch tags client-side if enabled
  const { data: clientTagsData, isLoading: isClientTagsLoading } =
    useRestaurantTags({
      params: {
        per_page: 100,
        order_dir: 'asc',
        order_by: 'order_index',
        restaurant_type:
          vendorType === 'online_shop' ? SHOP_TYPES.online_shop : undefined,
      },
      locale: lang,
      enabled: fetchTagsClientSide,
    });

  // Use client-side tags if fetching client-side, otherwise use server-side tags
  const tagList = fetchTagsClientSide ? clientTagsData : serverTagList;
  const isTagsLoading = fetchTagsClientSide ? isClientTagsLoading : false;

  useEffect(() => {
    let city: string | null = null;
    if (pathname?.includes('/city/')) {
      city = extractCityNameFromUrl(pathname);
      city = city
        ? (city as string)?.charAt(0).toUpperCase() +
          (city as string).slice(1).toLowerCase()
        : '';
    } else if (cityQueryParam) {
      city =
        cityQueryParam.charAt(0).toUpperCase() +
        cityQueryParam.slice(1).toLowerCase();
    }
    if (city) {
      setSelectedCityOption({
        id: -1,
        name: city,
        slug: city.toLowerCase(),
      });
    } else {
      setSelectedCityOption(initialSelectOption);
    }
  }, [cityQueryParam, pathname]);

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const params = new URLSearchParams(searchParams.toString());

    if (searchQuery) {
      params.delete('tag_ids');
      params.set('search', searchQuery);
    } else {
      params.delete('search');
    }
    replace(`${pathname}?${params.toString()}`);
  };

  const changeDeliveryType = (type: SearchTypes) => {
    if (type === shippingType) {
      return;
    }
    const params = new URLSearchParams(searchParams.toString());
    if (type === 'all') {
      params.delete('type');
    } else {
      params.set('type', type);
    }
    params.delete('free_delivery');
    const city = extractCityNameFromUrl(pathname);
    if (city) {
      params.set('city', city);
    }
    replace(`${pathname}?${params.toString()}`);
  };

  const handleKeywordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    if (e.target.value === '') {
      const params = new URLSearchParams(searchParams.toString());
      params.delete('search');
      replace(`${pathname}?${params.toString()}`);
    }
  };

  const handleCitySelect = (option: SelectOptionType) => {
    setSelectedCityOption(option);
    router.push(storeFront.city.de() + `/${option.slug}`);
  };

  return (
    <div className="search-area">
      <div className="container">
        <div className="mx-auto max-w-[869px] pb-[80px] pt-[30px] lg:pb-[103px] lg:pt-[60px]">
          {/* <h1 className="px-10 text-center text-[50px]/[1.3] font-[900] lg:px-0 lg:text-h1">
            {t(`${STOREFRONT.SEARCH}.header`)}
          </h1> */}
          {/* <p className="mb-[20px] mt-[46px] text-center text-lg text-paragraph lg:hidden">
            For Delivery and Pickup
          </p> */}
          <form
            onSubmit={handleSearch}
            className="mt-[46px] flex flex-col items-center justify-center md:mt-[60px] md:flex-row"
          >
            <div className="relative flex w-full items-center md:w-auto">
              <input
                name="keyword"
                type="search"
                placeholder={t(`${STOREFRONT.SEARCH}.placeholder`)}
                aria-label={t(`${STOREFRONT.SEARCH}.placeholder`)}
                defaultValue={searchParams.get('search')?.toString()}
                className="searchPageSearch h-[66px] w-full rounded-full border-0 bg-white py-[21px] pl-7 shadow-[0px_14px_70px_rgba(0,0,0,0.1)] outline-0 focus:border-0 focus:ring-0 md:w-[503px]"
                onChange={handleKeywordChange}
              />

              <Button
                rounded
                type="submit"
                className="absolute right-[1px] h-[66px] rounded-l-none rounded-r-full border-2 border-primary-700 px-[20px] py-[21px] transition-colors hover:bg-[#ef7588] sm:px-[40px]"
              >
                <MagnifyingGlassIcon className="h-6 w-6 text-white" />
              </Button>
            </div>
          </form>
          {pathname?.includes('/shops') && (
            <>
              <div className="mt-7 flex flex-col space-y-6 sm:flex-row sm:space-x-5 sm:space-y-0">
                {/* <div className="lg:w-1/2"> */}
                <OnlineShopCard
                  key={'hero-online-shop-card'}
                  cityThumbUrl={'/hero-section/online-shops-landscape.png'}
                  cityName={t(
                    STOREFRONT.HOME + '.hero_section.online_shop_card_title'
                  )}
                  countPlaces={cityData?.restaurants_count ?? 0}
                  citySlug={storeFront.onlineShops.de() + '?tag_ids=228'}
                  places={t('places')}
                  wrapperClassName="h-[200px]"
                />
                {/* </div>
                <div className="lg:w-1/2"> */}
                <CityNavigateCard
                  lang={lang}
                  cardData={null}
                  setCityData={(resData) => setCityData(resData?.online_shop)}
                  wrapperClassName="h-[200px]"
                  citySelectRowClasses="md:flex-row md:items-center md:justify-start md:space-x-2 md:space-y-0"
                  bgImgPath="/hero-section/cities-landscape.png"
                />
                {/* </div> */}
              </div>
            </>
          )}
        </div>
        {isTagsLoading ? (
          <div className="mb-20 flex items-center justify-center">
            <div className="mx-auto w-full rounded-md border border-gray-100 p-4 shadow-sm">
              <div className="flex animate-pulse space-x-4">
                <div className="flex-1 space-y-6 py-1">
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="col-span-1 h-20 rounded bg-slate-200"></div>
                      <div className="col-span-1 h-20 rounded bg-slate-200"></div>
                      <div className="col-span-1 h-20 rounded bg-slate-200"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          showTagsCarousel &&
          tagList &&
          tagList?.data?.length > 0 && (
            <>
              <TagsContainer tagsData={tagList} />
            </>
          )
        )}
        {vendorType !== 'online_shop' && (
          <div
            className={cn(
              'flex flex-col items-start justify-between gap-y-4 sm:flex-row sm:items-center sm:gap-0',
              !showShippingButtons && 'justify-end'
            )}
          >
            {showShippingButtons && (
              <SearchOptionButtons
                shippingType={shippingType}
                changeDeliveryType={changeDeliveryType}
              />
            )}

            <div className="flex items-center space-x-4">
              {/* <FreeDeliveryFilter shippingType={shippingType} /> */}
              {pathname?.includes('/city') && (
                <SelectWithRemoteSearch
                  apiEndpoint={env.api_url + '/v1/customer/cities'}
                  selectedOption={selectedCityOption}
                  onSelect={handleCitySelect}
                  paramKey="name"
                  placeholder={t('common.search_city')}
                  initalParams={{
                    is_favourite: 0,
                  }}
                  extraParams={{
                    order_by: 'order_index',
                    order_dir: 'asc',
                    per_page: 20,
                  }}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
